/**
 * İZİNLER — talepler, yıllık izin bakiyeleri, izin türleri.
 *
 * Eski `LeaveRequests.js`in yerine geldi: o sayfa onaylanan izni puantaja
 * yazmıyordu, hak edişi elle istiyordu ve menüde hiç yoktu.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { trNorm } from '@/lib/arama';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, AlertTriangle, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { YeniIzinDialog, TalepTablosu } from './izinOrtak';

const IZIN_DURUMLARI = [
  { deger: 'annual_leave', etiket: 'Yıllık İzin (bakiyeden düşer)' },
  { deger: 'excuse_leave', etiket: 'Mazeret İzni (ücretli)' },
  { deger: 'sick_leave', etiket: 'Rapor' },
  { deger: 'unpaid_leave', etiket: 'Ücretsiz İzin' },
];

export default function Izinler() {
  usePageTitle('İzinler');
  const { hasPermission } = useAuth();
  const onayYetkisi = hasPermission('timesheet', 'approve');
  const duzenleYetkisi = hasPermission('employees', 'edit');

  const [talepler, setTalepler] = useState([]);
  const [filtre, setFiltre] = useState('beklemede');
  const [yeniAcik, setYeniAcik] = useState(false);

  const talepleriYukle = useCallback(() => {
    api.get('/izin/talepler', { params: filtre === 'hepsi' ? {} : { durum: filtre } })
      .then(r => setTalepler(r.data || []))
      .catch(() => toast.error('İzin talepleri okunamadı'));
  }, [filtre]);
  useEffect(() => { talepleriYukle(); }, [talepleriYukle]);

  return (
    <div data-testid="izinler-sayfasi">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">İzinler</h1>
          <p className="text-sm text-[#8a7e70]">
            Onaylanan izin puantaja işlenir. Onay yetkisi puantaj / tahakkuk yetkilisindedir.
          </p>
        </div>
        {duzenleYetkisi && (
          <Button onClick={() => setYeniAcik(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]"
            data-testid="yeni-izin-talebi">
            <Plus size={16} className="mr-1" /> Yeni İzin Talebi
          </Button>
        )}
      </div>

      <Tabs defaultValue="talepler">
        <TabsList className="mb-4">
          <TabsTrigger value="talepler">Talepler</TabsTrigger>
          <TabsTrigger value="bakiyeler">Yıllık İzin Bakiyeleri</TabsTrigger>
          <TabsTrigger value="turler">İzin Türleri</TabsTrigger>
        </TabsList>

        <TabsContent value="talepler">
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardContent className="p-0">
              <div className="flex gap-1 p-3 border-b border-[#d0c4b0] flex-wrap">
                {[['beklemede', 'Bekleyenler'], ['onaylandi', 'Onaylananlar'], ['reddedildi', 'Reddedilenler'],
                  ['iptal', 'İptaller'], ['hepsi', 'Tümü']].map(([d, e]) => (
                  <button key={d} type="button" onClick={() => setFiltre(d)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors
                      ${filtre === d ? 'bg-[#2e2d29] text-[#f5f0e8]' : 'text-[#3F3E39] hover:bg-[#e8dfd0]'}`}>
                    {e}
                  </button>
                ))}
              </div>
              <TalepTablosu talepler={talepler} onayYetkisi={onayYetkisi} duzenleYetkisi={duzenleYetkisi}
                onDegisti={talepleriYukle} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bakiyeler"><BakiyelerTab /></TabsContent>
        <TabsContent value="turler"><TurlerTab onayYetkisi={onayYetkisi} /></TabsContent>
      </Tabs>

      <YeniIzinDialog open={yeniAcik} onClose={() => setYeniAcik(false)}
        onKaydedildi={() => { setFiltre('beklemede'); talepleriYukle(); }} />
    </div>
  );
}

function BakiyelerTab() {
  const navigate = useNavigate();
  const [liste, setListe] = useState([]);
  const [arama, setArama] = useState('');
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => {
    api.get('/izin/bakiyeler').then(r => setListe(r.data || []))
      .catch(() => toast.error('Bakiyeler okunamadı'))
      .finally(() => setYukleniyor(false));
  }, []);

  const gorunen = useMemo(() => {
    const q = trNorm(arama);
    return q ? liste.filter(b => trNorm(b.ad).includes(q)) : liste;
  }, [liste, arama]);
  const acilissiz = liste.filter(b => !b.acilis_girildi).length;

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardContent className="p-0">
        <div className="flex items-center gap-3 p-3 border-b border-[#d0c4b0] flex-wrap">
          <div className="relative w-64">
            <Search size={14} className="absolute left-2.5 top-2.5 text-[#8a7e70]" />
            <Input value={arama} onChange={(e) => setArama(e.target.value)} placeholder="Personel ara..."
              className="pl-8 h-9 bg-white border-[#d0c4b0]" />
          </div>
          {acilissiz > 0 && (
            <span className="flex items-center gap-1.5 text-xs text-[#5c4f22] bg-[#7a6a2e]/10 rounded-md px-2.5 py-1.5">
              <AlertTriangle size={13} />
              {acilissiz} personelin açılış bakiyesi girilmedi — kalanları olduğundan yüksek görünebilir.
              Açılışı personel kartındaki İzin sekmesinden girin.
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                {['Personel', 'İşe Giriş', 'Kıdem', 'Hak Edilen', 'Kullanılan', 'Planlanan', 'Kalan', 'Sonraki Hak Ediş']
                  .map((b, i) => (
                    <TableHead key={b} className={`text-[#1a1916] font-bold text-xs uppercase ${i >= 2 && i <= 6 ? 'text-right' : ''}`}>{b}</TableHead>
                  ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {yukleniyor && <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-[#8a7e70]">Yükleniyor…</TableCell></TableRow>}
              {!yukleniyor && gorunen.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-[#8a7e70]">Kayıt yok</TableCell></TableRow>
              )}
              {gorunen.map(b => (
                <TableRow key={b.employee_id} className="border-[#d0c4b0] cursor-pointer hover:bg-[#f5f0e8]"
                  onClick={() => navigate(`/erp/hr/employees/${b.employee_id}?sekme=izin`)}>
                  <TableCell className="text-sm font-medium text-[#1a1916]">
                    {b.ad}
                    {!b.acilis_girildi && <span className="ml-2 text-[10px] text-[#7a6a2e]" title="Açılış bakiyesi girilmedi">● açılış yok</span>}
                    {b.uyari && <p className="text-[10px] text-[#a34b3f]">{b.uyari}</p>}
                  </TableCell>
                  <TableCell className="text-sm text-[#3F3E39] whitespace-nowrap">{b.hire_date || '—'}</TableCell>
                  <TableCell className="text-sm text-right">{b.kidem_yil} yıl</TableCell>
                  <TableCell className="text-sm text-right font-mono">{b.hak_edilen}</TableCell>
                  <TableCell className="text-sm text-right font-mono">{b.kullanilan}</TableCell>
                  <TableCell className="text-sm text-right font-mono">{b.planlanan}</TableCell>
                  <TableCell className={`text-sm text-right font-mono font-bold ${b.kalan < 0 ? 'text-[#a34b3f]' : 'text-[#3d6e4e]'}`}>
                    {b.kalan.toLocaleString('tr-TR')}
                  </TableCell>
                  <TableCell className="text-sm text-[#3F3E39] whitespace-nowrap">
                    {b.sonraki_hak_edis ? `${b.sonraki_hak_edis.tarih} · ${b.sonraki_hak_edis.gun} gün` : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function TurlerTab({ onayYetkisi }) {
  const [turler, setTurler] = useState([]);
  const [yeni, setYeni] = useState({ ad: '', puantaj_durumu: 'excuse_leave', azami_gun: '' });
  const [duzenlenen, setDuzenlenen] = useState(null);

  const yukle = useCallback(() => {
    api.get('/izin/turler', { params: { hepsi: true } }).then(r => setTurler(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { yukle(); }, [yukle]);

  const ekle = async () => {
    if (!yeni.ad.trim()) return;
    try {
      await api.post('/izin/turler', yeni);
      toast.success('İzin türü eklendi');
      setYeni({ ad: '', puantaj_durumu: 'excuse_leave', azami_gun: '' }); yukle();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Eklenemedi'); }
  };

  const guncelle = async (id, alan) => {
    try { await api.patch(`/izin/turler/${id}`, alan); setDuzenlenen(null); yukle(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Güncellenemedi'); }
  };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardContent className="p-0">
        {onayYetkisi && (
          <div className="flex items-center gap-2 p-3 border-b border-[#d0c4b0] flex-wrap">
            <Input value={yeni.ad} onChange={(e) => setYeni(p => ({ ...p, ad: e.target.value }))}
              placeholder="Yeni izin türü (ör. Süt İzni)" className="h-9 w-56 bg-white border-[#d0c4b0]" />
            <select value={yeni.puantaj_durumu} onChange={(e) => setYeni(p => ({ ...p, puantaj_durumu: e.target.value }))}
              className="h-9 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm">
              {IZIN_DURUMLARI.map(d => <option key={d.deger} value={d.deger}>{d.etiket}</option>)}
            </select>
            <Input type="number" min="0" value={yeni.azami_gun} onChange={(e) => setYeni(p => ({ ...p, azami_gun: e.target.value }))}
              placeholder="Yasal gün" className="h-9 w-28 bg-white border-[#d0c4b0]" />
            <Button size="sm" onClick={ekle} disabled={!yeni.ad.trim()} className="h-9 bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
              <Plus size={14} className="mr-1" /> Ekle
            </Button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ad</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Puantajda</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Yasal Süre</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {turler.map(t => (
              <TableRow key={t.id} className="border-[#d0c4b0]">
                <TableCell className="text-sm font-medium text-[#1a1916]">
                  {duzenlenen?.id === t.id ? (
                    <div className="flex gap-1">
                      <Input value={duzenlenen.ad} autoFocus onChange={(e) => setDuzenlenen(p => ({ ...p, ad: e.target.value }))}
                        className="h-8 w-48 bg-white border-[#d0c4b0]" />
                      <Button size="sm" variant="ghost" onClick={() => guncelle(t.id, { ad: duzenlenen.ad })}><Check size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setDuzenlenen(null)}><X size={14} /></Button>
                    </div>
                  ) : (
                    <>{t.ad}{t.sistem && <span className="ml-2 text-[10px] text-[#8a7e70]">varsayılan</span>}</>
                  )}
                </TableCell>
                <TableCell className="text-sm text-[#3F3E39]">
                  {t.puantaj_etiketi}{t.yillik_bakiyeden_duser && <span className="text-[#8a7e70]"> · bakiyeden düşer</span>}
                </TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{t.azami_gun ? `${t.azami_gun} gün` : '—'}</TableCell>
                <TableCell>
                  {t.is_active
                    ? <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e]">Aktif</span>
                    : <span className="status-badge bg-[#8a7e70]/10 text-[#8a7e70]">Pasif</span>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {onayYetkisi && (
                    <>
                      <Button size="sm" variant="ghost" title="Adını değiştir" onClick={() => setDuzenlenen({ id: t.id, ad: t.ad })}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => guncelle(t.id, { is_active: !t.is_active })}
                        className="text-xs text-[#3F3E39]">
                        {t.is_active ? 'Pasife al' : 'Aktif et'}
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
